import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, Home as HomeIcon, Car, ArrowRight, ShieldCheck, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import VaelithChat from "@/components/VaelithChat";
import AIBriefing from "@/components/AIBriefing";
import AIPicks from "@/components/AIPicks";

const BG_IMAGES = [
  "https://images.unsplash.com/photo-1505843513577-22bb7d21e455?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000",
  "https://images.unsplash.com/photo-1535448580089-c7f9490c78b1?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000",
  "https://images.unsplash.com/photo-1583765748076-cac46b8c98c1?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000",
  "https://images.unsplash.com/photo-1532581140115-3e355d1ed1de?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000",
  "https://images.unsplash.com/photo-1566908829550-e6551b00979b?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000",
  "https://images.pexels.com/photos/26563767/pexels-photo-26563767.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=2000",
];

export default function Home() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [bg, setBg] = useState(0);

  useEffect(() => {
    BG_IMAGES.forEach((src) => { const im = new Image(); im.src = src; });
    const t = setInterval(() => setBg((i) => (i + 1) % BG_IMAGES.length), 5000);
    return () => clearInterval(t);
  }, []);

  return (
    <div>
      {/* ---------------- AI HERO (chat box is the search) ---------------- */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0" data-testid="hero-background">
          {BG_IMAGES.map((src, i) => (
            <div key={i} className="absolute inset-0 transition-opacity ease-in-out" style={{ opacity: i === bg ? 1 : 0, transitionDuration: "1600ms" }}>
              <img src={src} alt="" className="w-full h-full object-cover" />
            </div>
          ))}
          <div className="absolute inset-0 bg-black/70" />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent" />
          <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)", backgroundSize: "22px 22px" }} />
        </div>

        <div className="relative z-10 max-w-[820px] mx-auto px-5 sm:px-8 w-full pt-20 pb-16 text-center">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <div className="inline-flex items-center gap-2 text-accent font-mono-data text-[11px] uppercase tracking-[0.3em] mb-6">
              <Sparkles className="w-4 h-4" /> AI-Powered Luxury Intelligence
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-medium text-white leading-[1.03] max-w-3xl mx-auto">
              Ask anything. We answer with <span className="text-accent">verified data.</span>
            </h1>
            <p className="text-white/75 text-base sm:text-lg mt-5 max-w-2xl mx-auto leading-relaxed">
              The AI concierge for luxury real estate & exotic vehicles — search, analyze, and negotiate, grounded in verified market data. Never fabricated.
            </p>
          </motion.div>

          {/* Chat box as the primary entry point */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15 }}
            className="mt-9" id="home-ai-chat">
            {user ? (
              <div className="glass border border-border rounded-2xl shadow-2xl overflow-hidden flex flex-col h-[560px] text-left"
                data-testid="home-chat-surface" style={{ boxShadow: "0 0 60px -18px rgba(255,255,255,0.18)" }}>
                <div className="px-4 pt-3.5 pb-3 border-b border-border shrink-0 flex items-center gap-2">
                  <span className="relative flex h-7 w-7 items-center justify-center rounded-full bg-accent/15 border border-accent/30">
                    <Sparkles className="w-3.5 h-3.5 text-accent" />
                  </span>
                  <div>
                    <div className="font-display font-semibold text-sm leading-none">Vaelith AI</div>
                    <div className="font-mono-data text-[10px] text-muted-foreground uppercase tracking-wider mt-1">Search · Analyze · Compare · Negotiate</div>
                  </div>
                </div>
                <VaelithChat variant="inline" />
              </div>
            ) : (
              <div className="max-w-md mx-auto rounded-2xl border border-accent/30 bg-background/70 backdrop-blur-xl shadow-2xl p-6 text-center"
                data-testid="hero-signin-cta" style={{ boxShadow: "0 0 40px -12px rgba(255,255,255,0.15)" }}>
                <Sparkles className="w-6 h-6 text-accent mx-auto mb-3" />
                <p className="text-white text-base font-display mb-4">Sign in to ask Vaelith AI about luxury homes and vehicles.</p>
                <button onClick={() => navigate("/login")} data-testid="hero-signin-button"
                  className="inline-flex items-center gap-2 rounded-xl bg-accent text-accent-foreground px-5 py-3 text-sm font-medium hover:opacity-90 transition-opacity">
                  Sign in to ask <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            )}
            <div className="mt-5 inline-flex items-center gap-1.5 text-white/50 font-mono-data text-[10px] uppercase tracking-wider">
              <ShieldCheck className="w-3.5 h-3.5" /> Grounded in verified data — never fabricated
            </div>
          </motion.div>
        </div>
      </section>

      {/* ---------------- SIGNED-IN AI MODULES ---------------- */}
      {user && (
        <section className="max-w-[1600px] mx-auto px-5 sm:px-8 py-16 space-y-14">
          <AIBriefing />
          <AIPicks />
        </section>
      )}

      {/* ---------------- EXPLORE CARDS ---------------- */}
      <section className="max-w-[1600px] mx-auto px-5 sm:px-8 pb-24 pt-4 grid md:grid-cols-3 gap-6">
        {[
          { icon: HomeIcon, title: "Luxury Homes", desc: "Verified MLS/IDX listings across 30 states, bucketed by city and priced from $1M up.", to: "/homes" },
          { icon: Car, title: "Luxury Vehicles", desc: "Verified exotic & luxury inventory with live VIN decoding via NHTSA.", to: "/vehicles" },
          { icon: Search, title: "Market Intelligence", desc: "Bloomberg-grade dashboards across the world's top luxury markets.", to: "/market" },
        ].map((c) => (
          <motion.div key={c.title} whileHover={{ y: -6 }}
            onClick={() => navigate(user ? c.to : "/login")} data-testid={`feature-card-${c.title.split(" ")[1].toLowerCase()}`}
            className="bg-surface border border-border rounded-xl p-8 cursor-pointer transition-all hover:border-accent/40 hover:shadow-lg group">
            <c.icon className="w-6 h-6 mb-5 text-accent" strokeWidth={1.5} />
            <h3 className="font-display text-xl font-medium">{c.title}</h3>
            <p className="text-muted-foreground text-sm mt-2 leading-relaxed">{c.desc}</p>
            <div className="flex items-center gap-1 text-sm mt-5 group-hover:gap-2 transition-all">Explore <ArrowRight className="w-4 h-4" /></div>
          </motion.div>
        ))}
      </section>
    </div>
  );
}
